import { useEffect, useRef, useState } from "react";

import {
  CopyTextIcon,
  CopyTextIconProps,
} from "src/base-components/CopyTextIcon";
import { Pill } from "src/design-system/Pill";

type TruncatedIdPillProps = {
  id: string;
  dataLoc?: string;
  feedback?: CopyTextIconProps["feedback"];
  variant?: "last-five" | "middle-truncated" | "full";
};

const getTruncatedText = (
  id: string,
  variant: TruncatedIdPillProps["variant"],
) => {
  if (variant === "last-five") {
    return id.length > 5 ? "..." + id.slice(-5) : id;
  }

  if (variant === "middle-truncated") {
    if (id.length < 8) return id;
    const quarterLength = Math.floor(id.length / 4);
    return `${id.slice(0, quarterLength)}...${id.slice(-quarterLength)}`;
  }

  return id;
};

export const TruncatedIdPill: React.FC<TruncatedIdPillProps> = ({
  id,
  dataLoc,
  feedback,
  variant,
}) => {
  return (
    <Pill dataLoc={dataLoc ?? "id-pill"} size="sm" variant="gray" maxWidth>
      {variant ? (
        <div className="overflow-hidden">
          <p className="whitespace-nowrap font-fira-code">
            {getTruncatedText(id, variant)}
          </p>
        </div>
      ) : (
        <AutoTrancatedId id={id} />
      )}
      <CopyTextIcon
        dataLoc={`copy-id-${id}`}
        feedback={feedback}
        size="3xs"
        value={id}
      />
    </Pill>
  );
};

const AutoTrancatedId: React.FC<{ id: string }> = ({ id }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLDivElement>(null);
  const [truncatedText, setTruncatedText] = useState(id);

  useEffect(() => {
    const text = textRef.current;
    const container = containerRef.current;
    if (!container || !text) return;

    // If it fits, show everything
    text.textContent = id;
    if (text.scrollWidth <= container.clientWidth) {
      setTruncatedText(getTruncatedText(id, "full"));
      return;
    }

    const middleTruncated = getTruncatedText(id, "middle-truncated");
    text.textContent = middleTruncated;

    if (text.scrollWidth > container.clientWidth) {
      setTruncatedText(getTruncatedText(id, "last-five"));
    } else {
      setTruncatedText(middleTruncated);
    }
  }, [id]);

  return (
    <div ref={containerRef} className="overflow-hidden">
      <p ref={textRef} className="whitespace-nowrap font-fira-code">
        {truncatedText}
      </p>
    </div>
  );
};
